import { useMutation, useQuery } from "@apollo/client";
import FileDownload from "@mui/icons-material/FileDownload";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { GET_STAFF_CERS_PRE_POST } from "hooks/useStaffCersPrePost";
import { useCallback, useMemo, useState } from "react";

import { TenantAutocomplete, TenantOption } from "../Analytics/TenantAutocomplete";
import { AttributionTable } from "./AttributionTable";
import { PrePostStatCards } from "./PrePostStatCards";
import { PrePostRow, PrePostTable } from "./PrePostTable";

const EXPORT_STAFF_CERS_PRE_POST = gql(/* GraphQL */ `
  mutation ExportStaffCersPrePost($tenantId: ID) {
    exportStaffCersPrePost(tenantId: $tenantId) {
      success
      downloadUrl
      errorMsg
    }
  }
`);

function ExportButton({ tenantId }: { tenantId?: string }) {
  const alerts = useAlerts();
  // Alerts + download fire from onCompleted rather than effects keyed on the
  // mutation's data: Apollo doesn't clear `data` between calls, so a repeat
  // outcome (second success, same errorMsg) never re-triggers an effect.
  const [exportMutation, { loading }] = useMutation(EXPORT_STAFF_CERS_PRE_POST, {
    onCompleted: (data) => {
      const result = data.exportStaffCersPrePost;
      if (result?.success) {
        alerts.success("Export ready");
      } else if (result?.errorMsg) {
        alerts.error(`Export failed: ${result.errorMsg}`);
      }
      if (result?.downloadUrl) {
        window.open(result.downloadUrl, "_blank");
      }
    },
    onError: (err) => alerts.error(`Export failed: ${err.message}`),
  });

  const handleClick = useCallback(() => {
    exportMutation({ variables: { tenantId } });
  }, [exportMutation, tenantId]);

  return (
    <Button
      size="medium"
      loading={loading}
      startIcon={<FileDownload />}
      onClick={handleClick}
      aria-label="Export"
    >
      Export
    </Button>
  );
}

export const PrePost = () => {
  useBreadcrumb({ label: "Pre/Post Encamp", to: "/staff/cers/pre-post" });

  const [tenant, setTenant] = useState<TenantOption | null>(null);
  const { data, loading, error } = useQuery(GET_STAFF_CERS_PRE_POST, {
    variables: { tenantId: tenant?.id },
    fetchPolicy: "cache-and-network",
  });
  const result = data?.staffCersPrePost;
  const fw = result?.facilityWeighted;

  const pooledRows: PrePostRow[] = useMemo(() => {
    // Pooled rows are period-shaped, not prior/after-split; map periods onto
    // the prior/after columns for rendering through PrePostTable.
    const prior = result?.pooled.find((p) => p.period === "PRIOR");
    const after = result?.pooled.find((p) => p.period === "AFTER");
    if (!prior && !after) return [];
    return [
      {
        key: "pooled",
        name: "All eligible submittals",
        priorSubmittals: prior?.totalSubmittals ?? 0,
        priorRatePct: prior?.ratePct ?? null,
        afterSubmittals: after?.totalSubmittals ?? 0,
        afterRatePct: after?.ratePct ?? null,
        deltaPct:
          prior?.ratePct != null && after?.ratePct != null
            ? Math.round((after.ratePct - prior.ratePct) * 10) / 10
            : null,
      },
    ];
  }, [result?.pooled]);

  const cupaRows: PrePostRow[] = useMemo(
    () =>
      (result?.byCupa ?? []).map((c) => ({
        key: c.cupaCode || c.displayName,
        name: c.displayName,
        priorSubmittals: c.priorSubmittals,
        priorRatePct: c.priorRatePct ?? null,
        afterSubmittals: c.afterSubmittals,
        afterRatePct: c.afterRatePct ?? null,
        deltaPct: c.deltaPct ?? null,
      })),
    [result?.byCupa]
  );

  const complexityRows: PrePostRow[] = useMemo(
    () =>
      (result?.byComplexity ?? []).map((b) => ({
        key: b.bucket,
        name: b.bucket === "1" ? "1 decided module" : `${b.bucket} decided modules`,
        priorSubmittals: b.priorSubmittals,
        priorRatePct: b.priorRatePct ?? null,
        afterSubmittals: b.afterSubmittals,
        afterRatePct: b.afterRatePct ?? null,
        deltaPct: b.deltaPct ?? null,
      })),
    [result?.byComplexity]
  );

  if (error) {
    return (
      <Box sx={{ pt: 3, px: 3 }}>
        <Alert severity="error">Failed to load the pre/post comparison: {error.message}</Alert>
      </Box>
    );
  }

  const empty = !loading && (fw?.facilitiesAnalyzed ?? 0) === 0;

  return (
    <Box sx={{ pt: 3, px: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6">Pre/Post-Encamp Rejection Comparison</Typography>
          <Typography variant="caption" color="text.secondary">
            Facilities with reviewed submittals both before and after their first verified
            FILING_COMPLETE report · a submittal counts as rejected if any reviewed module has a
            substantive rejection · all-time window
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <TenantAutocomplete value={tenant} onChange={setTenant} />
          <ExportButton tenantId={tenant?.id} />
        </Box>
      </Box>

      {result ? (
        <Alert severity="info">
          {/* Window prose mirrors LINK_WINDOW_BEFORE_DAYS/LINK_WINDOW_AFTER_DAYS in
              encamp-cers (not imported — that package drags server-only deps into
              the bundle); the export's About sheet carries the authoritative values. */}
          Methodology: report links are fuzzy (nearest verified Encamp filing within −3 to +30 days
          of the submittal date); unlinked After submittals count as out-of-band. Only facilities
          with reviewed submittals on both sides of their cutoff are compared.
        </Alert>
      ) : null}

      {empty ? (
        <Alert severity="info">
          {tenant
            ? "No matched facilities with decided submittals on both sides of an Encamp cutoff for this organization."
            : "No matched facilities with decided submittals on both sides of an Encamp cutoff."}
        </Alert>
      ) : null}

      <PrePostStatCards result={result} loading={loading && !result} />

      <PrePostTable
        title="Pooled (submittal-weighted)"
        nameHeader="Population"
        rows={pooledRows}
        loading={loading && !result}
      />

      <AttributionTable rows={result?.attribution ?? []} loading={loading && !result} />

      <PrePostTable
        title="By CUPA"
        nameHeader="CUPA"
        rows={cupaRows}
        loading={loading && !result}
      />

      <PrePostTable
        title="By Report Complexity"
        nameHeader="Complexity"
        rows={complexityRows}
        loading={loading && !result}
      />
    </Box>
  );
};
